// Customers register (read-only -- customers are created when a quote is accepted).
// Augmented with client risk: a per-row risk badge and a "Flagged" filter tab,
// fed by GET /api/customers/at-risk. The dashboard "Flagged clients" tile deep-links
// here with ?filter=at-risk (the query value stays "at-risk"; only the label reads
// "Flagged", with a tooltip listing what triggers a flag).
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { RegisterPage, orNone } from "./RegisterPage";
import type { ColumnDef } from "./RegisterPage";
import { customers } from "../../../lib/resources";
import type { Customer } from "../../../lib/resources";
import { customerFields, customerEmptyForm, customerToForm } from "./customerForm";
import { ApiError, api } from "../../../lib/api";
import type { RiskFlag } from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ConfirmDeleteModal } from "../../../components/ConfirmDeleteModal";
import { Button } from "../../../components/Button";
import { StatusPill } from "../../../components/StatusPill";
import { AccountSymbol } from "../../../components/AccountSymbol";

type RiskFilter = "all" | "at-risk" | "archived" | "non_contract";

// The risk chip: amber for a single flag, red for two or more. Hover shows the plain
// English reason for every active flag; clicking opens the customer to act on them. A Link around a
// StatusPill (design system Session 5): the link keeps its own navigation, the pill carries the look.
function RiskFlagLink({ customerId, flags }: { customerId: string; flags: RiskFlag[] | undefined }) {
  if (!flags || flags.length === 0) return <span className="text-ele-body opacity-50">-</span>;
  const title = flags.map((f) => `${f.label}: ${f.summary}`).join("\n");
  return (
    <Link
      to={`/office/customers/${customerId}`}
      title={title}
      // The whole row navigates (onRowClick below). stopPropagation so this badge's own Link is the
      // one that navigates, exactly as the Lift ID cell does on the Lifts register: without it both
      // fire and the same page is pushed onto history twice, so Back appears to do nothing.
      onClick={(e) => e.stopPropagation()}
      // max-w-full + the truncating pill keep the badge INSIDE its (bounded) Risk column, so a long
      // label (e.g. "Service overdue") can never spill right over the Delete action (Session ACX
      // Fix 2). The title carries the full reason(s).
      className="inline-flex max-w-full transition hover:brightness-125"
    >
      <StatusPill
        word={flags.length === 1 ? flags[0]!.label : `${flags.length} flags`}
        tone={flags.length >= 2 ? "red" : "amber"}
        className="max-w-full"
      />
    </Link>
  );
}

export function CustomersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // The whole row opens the customer (onRowClick on RegisterPage below). RegisterPage stays
  // router-free, so the navigation is supplied here.
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [riskByCustomer, setRiskByCustomer] = useState<Map<string, RiskFlag[]>>(new Map());
  const [filter, setFilter] = useState<RiskFilter>(searchParams.get("filter") === "at-risk" ? "at-risk" : "all");
  // Xero sync: a one-tap "Sync from Xero" button LINKS existing customers to their Xero
  // contact (it never creates new customers). reloadKey bumps to refresh the list after.
  const [syncing, setSyncing] = useState(false);
  // "warn" is the amber caution tone (Session ANA). The sync has three genuinely different outcomes,
  // not two: it worked, it failed, or it worked and is now waiting on a decision from the office.
  // The third used to be styled exactly like the first, so the one result that needs a human read as
  // routine good news.
  const [syncBanner, setSyncBanner] = useState<{ tone: "ok" | "warn" | "error"; text: string } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // One-time admin cleanup of junk Xero imports (customers with no V2 activity).
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  // Per-row delete (Session AI Item 6). The server blocks (409) a customer that still has sites,
  // contracts, quotes, jobs, projects, invoices, LOLER reports or complaints, and keeps the Xero
  // contact; the office types the customer name to confirm. Reuses the existing delete endpoint.
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function confirmDeleteCustomer() {
    if (!deletingCustomer) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await customers.remove(deletingCustomer.id);
      setDeletingCustomer(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      // The 409 carries the plain-English blocker list (e.g. "still has 2 sites and 1 contract").
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the customer. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  async function syncFromXero() {
    setSyncing(true);
    setSyncBanner(null);
    try {
      const r = await api.customers.syncXero();
      // The counts sentence is unchanged. More is said only when the sync held an archive back
      // (Session ANA): Xero said a customer's contact was archived, but somebody at that customer
      // can still sign in to the customer portal, and archiving them here would shut them out
      // overnight with nobody at ELE having decided that. Saying so in plain English, with who it
      // was and what it would cost, is the whole point of the guard reaching this page. If it is
      // dropped the customers are still protected, but the office never learns they need a decision,
      // so the situation simply repeats every night in silence.
      //
      // THE NAMES ARE THE PART THAT MAKES IT ACTIONABLE. Without them this banner asked the office to
      // go and archive somebody by hand while giving them no way on earth to find out who: the
      // held-back customers are deliberately NOT archived, so the Archived filter does not list them,
      // this page has no portal-login column, and the only other record is an audit row that no
      // office page, endpoint or chat tool serves. The server reads each name for its audit row
      // anyway, so carrying them here costs nothing.
      //
      // The list can be shorter than the count when a name could not be read, so the count leads the
      // sentence and the names follow it. When no name came back at all the colon clause is dropped
      // rather than left dangling, and the office still gets the count and the consequence.
      const namesPart = r.flaggedForReviewNames.length > 0 ? `: ${r.flaggedForReviewNames.join(", ")}` : "";
      // THE CONSEQUENCE HAS TO BE STATED. "Archive them by hand if that is right" on its own hides
      // the very thing the guard exists to prevent: archiving revokes that customer's portal logins.
      // The office cannot judge "if that is right" without being told what it costs. Reactivating the
      // customer is a true undo (the same logins start working again, with no new invite and no new
      // code), so the sentence says that too rather than implying the loss is permanent.
      const held =
        r.flaggedForReview > 0
          ? ` ${r.flaggedForReview} customer${r.flaggedForReview === 1 ? " was" : "s were"} not archived because they can still sign in to the customer portal${namesPart}.` +
            " Archive them by hand if that is right. Archiving stops those logins working until you reactivate the customer, which puts the same logins back."
          : "";
      // A contact NOT re-imported because the office erased that person (Session ASQ, Stream G, Item 3): the server's own
      // plain sentence per contact, "A contact erased on DD/MM/YYYY was not re-imported". Lifting the erasure record is on
      // the Personal data requests page, if the person has asked to come back.
      const erasedWarnings = (r.warnings ?? []).length > 0 ? ` ${(r.warnings ?? []).join(" ")}` : "";
      // Amber, not green, whenever something was held back: this is a result waiting on a decision,
      // not a clean run. Nothing failed either, so it is never the red error tone.
      setSyncBanner({
        tone: r.flaggedForReview > 0 || erasedWarnings ? "warn" : "ok",
        text: `Sync complete: ${r.linked} linked, ${r.updated} updated, ${r.archived} archived.${held}${erasedWarnings}`,
      });
      setReloadKey((k) => k + 1);
    } catch {
      setSyncBanner({ tone: "error", text: "Could not sync from Xero. Check the Xero connection in Settings and try again." });
    } finally {
      setSyncing(false);
    }
  }

  async function cleanupUnlinked() {
    setCleaning(true);
    setCleanupError(null);
    try {
      const r = await api.customers.cleanupUnlinked();
      setCleanupOpen(false);
      setSyncBanner({
        tone: "ok",
        text: r.removed === 0 ? "No unlinked imports found. Nothing was removed." : `Removed ${r.removed} unlinked import${r.removed === 1 ? "" : "s"}.`,
      });
      if (r.removed > 0) setReloadKey((k) => k + 1);
    } catch {
      setCleanupError("Could not remove unlinked imports. Please try again.");
    } finally {
      setCleaning(false);
    }
  }

  useEffect(() => {
    let live = true;
    api.customers
      .atRisk()
      .then((rows) => {
        if (live) setRiskByCustomer(new Map(rows.map((r) => [r.customerId, r.flags])));
      })
      .catch(() => {
        /* risk is supplementary; the list still works without it */
      });
    return () => {
      live = false;
    };
  }, []);

  const columns = useMemo<ColumnDef<Customer>[]>(
    () => [
      {
        key: "name",
        label: "Name",
        width: "21%",
        // Kept as a real link for keyboard focus and open-in-new-tab; it stops propagation so it
        // does not also fire the row click and push the same page twice.
        render: (c) => (
          <>
            {/* Session SC9B: the account symbol BEFORE the name (a truncating cell cuts the name, never the symbol); the register page already carries each row's status, so no extra read. */}
            <span onClick={(e) => e.stopPropagation()}>
              <AccountSymbol customerId={c.id} status={c.accountStatus} />
            </span>
            <Link to={`/office/customers/${c.id}`} onClick={(e) => e.stopPropagation()} className="text-ele-green hover:underline">
              {c.name}
            </Link>
          </>
        ),
        sortValue: (c) => c.name ?? "",
      },
      // The "Type" column is dropped from the list (it was always "Direct"); the client type still
      // shows on the customer detail page (Session AJ, Item 4).
      { key: "contact", label: "Contact", width: "16%", render: (c) => orNone(c.billingContact), sortValue: (c) => c.billingContact ?? "" },
      { key: "email", label: "Email", width: "20%", render: (c) => orNone(c.billingEmail), sortValue: (c) => c.billingEmail ?? "" },
      { key: "phone", label: "Phone", width: "12%", render: (c) => orNone(c.phone), sortValue: (c) => c.phone ?? "" },
      { key: "sites", label: "Sites", width: "7%", render: (c) => c._count?.sites ?? 0, sortValue: (c) => c._count?.sites ?? 0 },
      // Risk is a derived badge with no simple sortable value; left unsortable (sort by name etc.).
      // Widened (Session ACX Fix 2) so a typical badge fits without truncating; the badge itself is
      // also capped to its column (RiskFlagLink max-w-full) so it can never overlap the Delete action.
      { key: "risk", label: "Risk", width: "14%", noTruncate: true, render: (c) => <RiskFlagLink customerId={c.id} flags={riskByCustomer.get(c.id)} /> },
      // Per-row delete (the list is read-only for editing, but a customer can be deleted from here).
      {
        key: "actions",
        label: "",
        width: "10%",
        noTruncate: true,
        render: (c) =>
          c.archivedAt ? (
            <span className="text-xs text-ele-body opacity-60">Archived</span>
          ) : (
            <button
              type="button"
              // stopPropagation so Delete only opens the confirm dialog: without it the row click
              // would navigate away to the customer at the same time.
              onClick={(e) => {
                e.stopPropagation();
                setDeleteError(null);
                setDeletingCustomer(c);
              }}
              className="font-menu text-xs tracking-wide text-ele-danger transition hover:underline"
            >
              Delete
            </button>
          ),
      },
    ],
    [riskByCustomer],
  );

  const toolbar = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {(
            [
              { value: "all", label: "All" },
              {
                value: "at-risk",
                label: `Flagged${riskByCustomer.size ? ` (${riskByCustomer.size})` : ""}`,
                title:
                  "Customers with one or more open risk flags: overdue service, overdue LOLER, an overdue invoice, no recent contact, a high breakdown rate, or a low NPS score.",
              },
              { value: "archived", label: "Archived" },
              {
                value: "non_contract",
                label: "Non-contract",
                title:
                  "One-off callers created during a call-out report (Session AFM). Billed at double Schedule of Fees rates with no tier; hidden from the main list until promoted to a full customer.",
              },
            ] as { value: RiskFilter; label: string; title?: string }[]
          ).map((t) => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              title={t.title}
              className={`rounded-ele border px-4 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                filter === t.value
                  ? "border-ele-green bg-ele-inset text-ele-green"
                  : "border-ele-purple-accent text-ele-body hover:text-ele-green"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setCleanupError(null);
                setCleanupOpen(true);
              }}
            >
              Remove unlinked imports
            </Button>
          )}
          <button
            onClick={syncFromXero}
            disabled={syncing}
            className="rounded-ele border border-ele-purple px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green disabled:opacity-50"
          >
            {syncing ? "Syncing..." : "Sync from Xero"}
          </button>
        </div>
      </div>
      {syncBanner && (
        <p
          role="status"
          // Three tones for the three real outcomes. The amber caution treatment is the office's
          // existing one, copied from the initial-term warning in CancelContractModal.tsx rather
          // than invented here, so a caution looks the same everywhere in the office.
          className={`rounded-ele border px-4 py-2 text-sm ${
            syncBanner.tone === "error"
              ? "border-red-500/50 bg-red-500/10 text-ele-danger"
              : syncBanner.tone === "warn"
                ? "border-amber-500 bg-amber-500/10 text-amber-300"
                : "border-ele-green/50 bg-ele-green/10 text-ele-green"
          }`}
        >
          {syncBanner.text}
        </p>
      )}
    </div>
  );

  return (
    <>
    <RegisterPage<Customer>
      key={reloadKey}
      title="Customers"
      subtitle="The companies and people ELE bills."
      noun="customer"
      resource={customers}
      columns={columns}
      fields={customerFields}
      rowId={(c) => c.id}
      toForm={customerToForm}
      emptyForm={customerEmptyForm}
      readOnly
      note={
        filter === "archived"
          ? "Archived customers (retained for their signed-contract or invoice history). Open one to reactivate it."
          : filter === "non_contract"
            ? "One-off callers: created during a call-out report, billed at non-contract rates (double the Schedule of Fees, no tier). Open one to promote them to a full customer."
            : "Your customers in V2. Customers are created when a quote is accepted, or added by the office. Sync from Xero links existing customers to their Xero contact for billing; it never creates new ones. This page is read-only."
      }
      toolbar={toolbar}
      // Session SC2C: read 50 at a time; the SERVER applies the archived / non-contract / at-risk views, the search and the sort (GET /api/customers).
      serverPaged
      exportList="customers"
      query={filter === "archived" ? { archived: "archived" } : filter === "non_contract" ? { type: "non_contract" } : filter === "at-risk" ? { risk: "at-risk" } : undefined}
      // Clicking anywhere on a row opens the customer, not just the green name. Every interactive
      // cell (the name Link, the risk badge, Delete) stops propagation so it keeps its own
      // behaviour. The per-row Delete is this page's own column, not RegisterPage's built-in
      // actions column, so it is unaffected by onRowClick suppressing that one.
      onRowClick={(c) => navigate(`/office/customers/${c.id}`)}
      // Session ACW (mobile usability): below md keep the name and the risk badge (the identifier
      // + the one status-ish thing worth a glance); Contact/Email/Phone/Sites/the inline Delete
      // column hide. This is a read-only list with a real detail page per row
      // (/office/customers/:id), which carries its own Delete control, so hiding the inline Delete
      // here on a phone loses no capability. With onRowClick set, RegisterPage also shows its
      // mobile chevron disclosure, which is now correct: the whole row really does navigate.
      mobileColumns={["name", "risk"]}
      mobileLabel="Customers"
      // Mobile truncation verification pass: mobileColumns above narrows the DESKTOP table to two
      // columns below md, but RegisterPage's mobileColumns mechanism is the SAME non-renormalizing
      // fixed-layout colgroup pattern the Session AHB card pass fixed elsewhere -- keeping "name"
      // at its literal desktop share (21%) is not the same as giving it enough room. Measured: at
      // 375px the name column got roughly 48px of actual text space after padding, about 7 to 8
      // characters, badly truncating a real company name. mobileRow replaces the table with the
      // same two-line swipe row SitesPage.tsx already uses, so the name never truncates (it wraps)
      // and the risk flag becomes a plain pill (never a nested Link, since the whole row is itself
      // a tap target to the same destination the risk badge would have opened).
      mobileRow={(c) => {
        const flags = riskByCustomer.get(c.id);
        const meta =
          flags && flags.length > 0 ? (
            <StatusPill
              word={flags.length === 1 ? flags[0]!.label : `${flags.length} flags`}
              tone={flags.length >= 2 ? "red" : "amber"}
            />
          ) : undefined;
        return {
          title: (
            <>
              {c.name}
              <AccountSymbol plain customerId={c.id} status={c.accountStatus} />
            </>
          ),
          subtitle: orNone(c.billingContact),
          meta,
          onTap: () => navigate(`/office/customers/${c.id}`),
        };
      }}
    />
    <ConfirmDeleteModal
      open={cleanupOpen}
      title="Remove unlinked Xero imports"
      message="This permanently removes any customer that is linked to a Xero contact and has no sites, contracts, quotes, projects, invoices or other records in V2. These are usually contacts an earlier sync imported that never became real customers. A customer with any activity is never removed. Review the list first; this cannot be undone."
      confirmLabel="Remove imports"
      variant="destructive"
      busy={cleaning}
      error={cleanupError}
      onConfirm={cleanupUnlinked}
      onCancel={() => setCleanupOpen(false)}
    />
    <ConfirmDeleteModal
      open={deletingCustomer !== null}
      title={`Delete ${deletingCustomer?.name ?? ""}?`}
      message="This permanently deletes the customer. It is only allowed when the customer has no sites, contracts, quotes, jobs, projects, invoices, LOLER reports or complaints attached: if any exist the delete is blocked and lists what to remove first. The linked Xero contact is not deleted. This cannot be undone."
      confirmLabel="Delete customer"
      busy={deleting}
      error={deleteError}
      onConfirm={confirmDeleteCustomer}
      onCancel={() => {
        if (deleting) return;
        setDeletingCustomer(null);
        setDeleteError(null);
      }}
    />
    </>
  );
}
